import type { CartForwardBuySummary } from '@erp/rpc';
import type { BoardSharedFilters } from '../utils/boardSharedFilters';
import { ForwardBuyCartNote } from './ForwardBuyCartNote';
import {
  useEffect,
  useMemo,
  useRef,
  useLayoutEffect,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react';
import { T, useGT } from 'gt-react';
import { cn } from '@/lib/utils';
import { DataTable } from '@/components/common/data-table/data-table';
import { useDataTable } from '@/components/common/data-table/use-data-table';
import type {
  AssignmentLine,
  CartAssignment,
} from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import {
  getRowAccentClass,
  useBoardCartLines,
  useTrashLine,
  type CartLineTableMeta,
} from './cartLineColumns';
import { CartInclusionContext } from './cartInclusionContext';
import { QuantityDraftProvider } from './QuantityDraftProvider';
import { SectionSpinner } from './SectionSpinner';
import {
  LineBulkEditProvider,
  type LineBulkEditTargets,
} from './lineBulkEditContext';
import {
  useAssignmentLineHighlight,
  useClearSelectionKey,
} from './useAssignmentLineHighlight';
import { syncAssignmentLinesForBulkEdit } from '../utils/assignmentBulkEditSession';
import { createPortal } from 'react-dom';
import { CartDetailHeader } from './CartDetailHeader';
import { useDetailHeaderSlot } from './detailHeaderSlot';
import { ReleaseCartButton } from './ReleaseCartButton';
import { OpenDraftPurchaseOrders } from './OpenDraftPurchaseOrders';
import { AssignmentCartRail } from './AssignmentCartRail';
import { AssignmentToolbar } from './AssignmentToolbar';
import { useAssignmentModel } from './useAssignmentModel';
import { useLineGroupTable } from './lineGroupTable';
import { NeedRowsContext } from './needRowsContext';
import {
  expandNeedIds,
  isNeedId,
  shownSelectedLineId,
  type NeedGroup,
} from '../utils/needRows';
import { assignmentVisibleLineIds } from './assignmentVisibleLineIds';
import { preventAssignmentLineModifierSelect } from './assignmentBoardRowClick';
import { useLineTableColumns } from './lineTableColumns';
import { lineRowDomId, useLineStepKeys } from './useLineStepKeys';
import {
  cartOwnerId,
  type CartLinesQuery,
  type CartOwner,
  type SupplierAssignmentsQuery,
} from './supplierAssignmentsInput';
import { useCartLines } from './useCartLines';
import { useCartOrderReadiness } from './cartOrderReadiness';
import { LineTotalsRow } from './LineTotalsRow';
import { computeLineTotals, type LineTotals } from '../utils/lineTotals';
import { TableViewMenu, type TableView } from './TableViewMenu';
import { useColumnsHiddenByFilters } from './useColumnsHiddenByFilters';
import { useOverrideDisplay } from './useOverrideDisplay';
import { useBuyerBoardColumnConfig } from './useBuyerBoardColumnConfig';
import { useBuyerBoardTableState } from './useBuyerBoardTableState';
import {
  columnVisibilityKey,
  filterColumnsShownByView,
  lineColumnVisibility,
} from '../utils/buyerBoardColumns';
import { groupLines } from '../utils/lineGroups';
import { lineFilterColumns } from '../utils/lineFilterColumns';
import {
  ALL_LINE_TYPES,
  type BuyingOrderLineType,
} from '../utils/buyingOrdersUrl';
import {
  EMPTY_CART_STAT,
  type CartCreatePlan,
  type CartCreateSummary,
  type CartStat,
  type DesignationFilter,
} from '../utils/assignmentCarts';

export type { CartCreateSummary };

interface AssignmentBoardProps {
  /** Whose carts: a vendor's (buyer board) or a source location's (transfers). */
  owner: CartOwner;
  /** Composite multi-division filter (supersedes the single `divisionId`). */
  divisionIds?: string[];
  /** Destination-warehouse filter, scoped within the selected divisions. */
  warehouseIds?: string[];
  /** Include trashed (rejected) lines (board-wide "Show trashed"). */
  includeTrashed?: boolean;
  /** Include lines released since the last refresh (shown read-only). */
  includeReleased?: boolean;
  /** Buy-vs-transfer row filter from the board's view toggle. */
  designationFilter?: DesignationFilter;
  /** Line types the page filter keeps; others are hidden. Default: all. */
  lineTypes?: readonly BuyingOrderLineType[];
  /** Buyer codes the page filter keeps; the server drops the other lines. */
  buyerCodes?: string[];
  /** The shared procurement line filters, applied the same way. */
  lineFilters?: BoardSharedFilters;
  /**
   * Detail mode: load and show only this ship-to's cart, with the one-line
   * cart header (and its close button) in place of the cart chip rail.
   */
  shipToWarehouseId?: string;
  onClose?: () => void;
  /** The line shown in the Line section below, highlighted in the table. */
  selectedLineId?: string;
  /** A row click selects its line (again: clears it). */
  onSelectLine: (suggestionId: string) => void;
  /** Close the Line section (Clear selection). */
  onClearLine: () => void;
  /** How many lines are selected, for the Line section's "1 of N". */
  onSelectedCountChange?: (count: number) => void;
  /** Turns off ↑/↓ line stepping, e.g. while a dialog owns the keyboard. */
  lineKeysDisabled?: boolean;
  enabled: boolean;
  /** Keys (`${ownerId}:${shipToWarehouseId}`) of carts with an in-flight create. */
  creatingCartKeys: Set<string>;
  /** Create POs/TOs for one cart's included lines (cart = this owner × ship-to). */
  onCreateCart: (
    shipToWarehouseId: string,
    summary: CartCreateSummary,
    releaseSuggestionIds: string[],
  ) => void;
}

// Rows run 28px for a plain line up to ~54px with a price-break note.
const LINE_ROW_VIRTUALIZATION = { estimateRowHeight: 40 };

// fallow-ignore-next-line complexity
function useAssignmentBoardState(props: AssignmentBoardProps) {
  const {
    owner,
    warehouseIds,
    designationFilter,
    lineTypes,
    buyerCodes,
    lineFilters,
    shipToWarehouseId,
    selectedLineId,
    onSelectLine,
    onClearLine,
    onSelectedCountChange,
    lineKeysDisabled,
    enabled,
  } = props;
  const ownerId = cartOwnerId(owner);
  const { data, isLoading, isFetching } = useCartLines(props, enabled);

  const editTargetsRef = useRef<LineBulkEditTargets>((originId) => [originId]);
  const needsRef = useRef<ReadonlyMap<string, NeedGroup>>(new Map());
  // A need row (or a highlight over need rows) edits its open SO lines.
  const resolveEditTargets = (originId: string) =>
    expandNeedIds(editTargetsRef.current(originId), needsRef.current);

  // Buyer approvals: the lines the server has marked approved, shared
  // across this supplier's carts so the Create button can read it.
  const { lines, selectedIds, onSetApproval } = useBoardCartLines(
    data?.lines,
    resolveEditTargets,
  );

  const model = useAssignmentModel({
    carts: data?.carts,
    lines,
    designationFilter,
    selectedIds,
    supplierId: ownerId,
  });
  const columnConfig = useBuyerBoardColumnConfig();
  // Judge one-value columns on the rows the table renders (after line types).
  const renderedLines = groupLines(
    model.filteredLines,
    lineTypes ?? ALL_LINE_TYPES,
  ).lines;
  const lineColumns = useColumnsHiddenByFilters(
    filterColumnsShownByView(
      lineFilterColumns(
        {
          warehouseIds,
          buyerCodes,
          popularityCodes: lineFilters?.popularityCodes,
          stockStatuses: lineFilters?.stockStatuses,
          locationFocused: [
            model.filters.shipTo,
            model.filters.warehouse,
            model.filters.hub,
          ].some((focus) => focus.value.length > 0),
        },
        model.cartsByShipTo,
      ),
      columnConfig.columns,
    ),
    renderedLines,
  );
  const overrideDisplay = useOverrideDisplay(columnConfig);
  const view: LineTableView = {
    config: columnConfig,
    overrideDisplay,
    columns: lineColumns,
  };

  const highlightCartKey = `${ownerId}:${shipToWarehouseId ?? ''}`;

  const tableState = useAssignmentTable({
    filteredLines: model.filteredLines,
    lineTypes,
    view,
    selectedIds,
    onSetApproval,
    cartsByShipTo: model.cartsByShipTo,
    owner,
    ownerName: data?.ownerName,
    shipToWarehouseId,
    selectedLineId,
    editTargets: resolveEditTargets,
  });

  const visibleLineIds = assignmentVisibleLineIds(
    tableState.table,
    tableState.groupProps,
  );

  const assignmentLinesReady = !isFetching || lines.length > 0;

  const shownLineId = shownSelectedLineId(
    selectedLineId,
    visibleLineIds,
    model.filteredLines,
  );
  const {
    editTargets,
    handleLineRowClick,
    isBulkHighlighted,
    selectedCount,
    clearSelection,
  } = useLineSelection({
    cartKey: highlightCartKey,
    visibleLineIds,
    linesReady: assignmentLinesReady,
    selectedLineId: shownLineId,
    onSelectLine,
    onClearLine,
    lineKeysDisabled,
  });
  useLayoutEffect(() => {
    editTargetsRef.current = editTargets;
  }, [editTargets]);
  // The selection lives here; the Line section above the table's parent
  // only needs its size.
  // The page only reads the count for the Line header; lifting the whole
  // selection out of the board is not worth it.
  useEffect(() => {
    // react-doctor-disable-next-line react-doctor/no-pass-data-to-parent
    onSelectedCountChange?.(selectedCount);
  }, [selectedCount, onSelectedCountChange]);
  const needs = tableState.needRows?.needs;
  useLayoutEffect(() => {
    needsRef.current = needs ?? new Map();
  }, [needs]);
  useLayoutEffect(() => {
    syncAssignmentLinesForBulkEdit(highlightCartKey, lines, {
      linesReady: assignmentLinesReady,
    });
  }, [highlightCartKey, lines, assignmentLinesReady]);
  return {
    data,
    view,
    isLoading,
    isFetching,
    lines,
    selectedIds,
    model,
    tableState,
    handleLineRowClick,
    isBulkHighlighted,
    selectedCount,
    clearSelection,
    shownLineId,
    editTargets: resolveEditTargets,
  };
}

/** Click, Shift/Ctrl/Cmd-click and key selection of the line table's rows. */
function useLineSelection({
  cartKey,
  visibleLineIds,
  linesReady,
  selectedLineId,
  onSelectLine,
  onClearLine,
  lineKeysDisabled,
}: {
  cartKey: string;
  visibleLineIds: readonly string[];
  linesReady: boolean;
  selectedLineId: string | undefined;
  onSelectLine: (suggestionId: string) => void;
  onClearLine: () => void;
  lineKeysDisabled: boolean | undefined;
}) {
  const selection = useAssignmentLineHighlight(
    cartKey,
    visibleLineIds,
    onSelectLine,
    { linesReady, selectedLineId, onClearLine },
  );
  useClearSelectionKey(
    selection.selectedCount > 1,
    selection.clearSelection,
    lineKeysDisabled,
  );
  useLineStepKeys(
    visibleLineIds,
    selectedLineId,
    (suggestionId) => {
      selection.selectOnly(suggestionId);
      // At either end the line stays open; onSelectLine would toggle it shut.
      if (suggestionId !== selectedLineId) onSelectLine(suggestionId);
    },
    lineKeysDisabled,
  );
  return selection;
}

export function AssignmentBoard(props: AssignmentBoardProps) {
  const gt = useGT();
  const {
    data,
    view,
    isLoading,
    isFetching,
    lines,
    selectedIds,
    model,
    tableState,
    handleLineRowClick,
    isBulkHighlighted,
    selectedCount,
    clearSelection,
    shownLineId,
  } = useAssignmentBoardState(props);

  // Show the block spinner until a fetch has actually settled with a result.
  // `isLoading` alone is unreliable: it's false during a background refetch and
  // for the brief idle-pending mount frame. Gating on "no data yet OR a fetch
  // in flight" keeps the spinner up through both the first load and refetches.
  // The saved column view also gates the table, so it never draws in the
  // standard layout and then jumps to the buyer's.
  if (lines.length === 0 || view.config.isLoading) {
    return (
      <EmptyBoard
        loading={isFetching || data == null || view.config.isLoading}
        gt={gt}
      />
    );
  }

  const detailCart = openDetailCart(
    model,
    props.shipToWarehouseId,
    props.onClose,
  );
  // The table's footer sums the lines in view; an open order's header sums
  // its whole cart instead, the lines Release sends (see planForCart).
  const viewTotals = detailCart
    ? null
    : computeLineTotals(tableState.grouped.lines, selectedIds);
  return (
    <RefetchFrame refetching={isFetching && !isLoading} gt={gt}>
      <AssignmentBoardHeader
        query={props}
        model={model}
        detailCart={detailCart}
        view={view}
        owner={props.owner}
        ownerName={data?.ownerName ?? ''}
        onClose={props.onClose}
        creatingCartKeys={props.creatingCartKeys}
        onCreateCart={props.onCreateCart}
        selectedCount={selectedCount}
        onClearSelection={clearSelection}
        gt={gt}
      />

      <AssignmentLineTable
        {...tableState}
        isBulkHighlighted={isBulkHighlighted}
        selectedIds={selectedIds}
        selectedLineId={shownLineId}
        onRowClick={handleLineRowClick}
        totals={viewTotals}
      />
    </RefetchFrame>
  );
}

function EmptyBoard({
  loading,
  gt,
}: {
  loading: boolean;
  gt: ReturnType<typeof useGT>;
}) {
  if (loading) {
    return (
      <SectionSpinner
        variant="block"
        label={gt('Loading assignments…', {
          context: 'assignment board initial load spinner label',
        })}
      />
    );
  }
  return (
    <div className="flex items-center justify-center py-8 text-sm text-gray-400">
      <T context="assignment board empty">No suggestion lines found.</T>
    </div>
  );
}

function RefetchFrame({
  refetching,
  gt,
  children,
}: {
  refetching: boolean;
  gt: ReturnType<typeof useGT>;
  children: ReactNode;
}) {
  return (
    <div className="space-y-4 relative">
      {refetching && (
        <SectionSpinner
          variant="overlay"
          label={gt('Refreshing assignments…', {
            context: 'assignment board overlay refetch spinner label',
          })}
        />
      )}
      <div className={cn('transition-opacity', refetching && 'opacity-60')}>
        {children}
      </div>
    </div>
  );
}

type AssignmentModel = ReturnType<typeof useAssignmentModel>;
type LineTableView = Required<TableView>;

/**
 * The cart the detail pane has open, if any: its header then stands in for
 * the chip rail and carries the approved totals.
 */
function openDetailCart(
  model: AssignmentModel,
  shipToWarehouseId: string | undefined,
  onClose: (() => void) | undefined,
): CartAssignment | null {
  return shipToWarehouseId != null && onClose != null
    ? (model.cartsByShipTo.get(shipToWarehouseId) ?? null)
    : null;
}

function AssignmentBoardHeader({
  query,
  model,
  detailCart,
  view,
  owner,
  ownerName,
  onClose,
  creatingCartKeys,
  onCreateCart,
  selectedCount,
  onClearSelection,
  gt,
}: {
  query: CartLinesQuery;
  model: AssignmentModel;
  detailCart: CartAssignment | null;
  view: LineTableView;
  owner: CartOwner;
  ownerName: string;
  onClose: (() => void) | undefined;
  creatingCartKeys: Set<string>;
  onCreateCart: AssignmentBoardProps['onCreateCart'];
  selectedCount: number;
  onClearSelection: () => void;
  gt: ReturnType<typeof useGT>;
}) {
  const { filters } = model;
  const ownerId = cartOwnerId(owner);
  return (
    <div className="px-2 pt-2">
      {detailCart && onClose && (
        <AssignmentDetailHeader
          query={query}
          owner={owner}
          ownerName={ownerName}
          cart={detailCart}
          stat={model.cartStats.get(detailCart.shipToWarehouseId)}
          plan={model.planForCart(detailCart.shipToWarehouseId)}
          forwardBuy={
            model.forwardBuyByCart.get(detailCart.shipToWarehouseId) ?? null
          }
          creating={creatingCartKeys.has(
            `${ownerId}:${detailCart.shipToWarehouseId}`,
          )}
          onClose={onClose}
          onCreateCart={onCreateCart}
        />
      )}
      <AssignmentCartRail
        supplierId={ownerId}
        railCarts={model.railCarts}
        showChips={!detailCart}
        cartStats={model.cartStats}
        selectedShipTo={filters.url.selectedShipTo}
        forwardBoughtShipTos={model.forwardBoughtShipTos}
        creatingCartKeys={creatingCartKeys}
        planForCart={model.planForCart}
        onSelectShipTo={filters.url.selectShipTo}
        onCreateCart={onCreateCart}
        gt={gt}
      />
      <AssignmentToolbar
        search={filters.url.search}
        onSearch={filters.url.setSearch}
        chips={filters.url.chips}
        onToggleChip={filters.url.toggleChip}
        shipTo={filters.shipTo}
        warehouse={filters.warehouse}
        hub={filters.hub}
        filteredCount={model.filteredLines.length}
        scopedCount={model.scopedLines.length}
        selectedCount={selectedCount}
        onClearSelection={onClearSelection}
        viewMenu={
          <TableViewMenu
            view={view}
            columnLabels={Object.fromEntries(
              view.config.columns.map((column) => [column.id, column.header]),
            )}
          />
        }
        gt={gt}
      />
    </div>
  );
}

interface TableInput {
  filteredLines: AssignmentLine[];
  lineTypes: readonly BuyingOrderLineType[] | undefined;
  view: LineTableView;
  selectedIds: Set<string>;
  onSetApproval: CartLineTableMeta['onSetApproval'];
  cartsByShipTo: Map<string, CartAssignment>;
  owner: CartOwner;
  ownerName: string | undefined;
  shipToWarehouseId: string | undefined;
  selectedLineId: string | undefined;
  editTargets: LineBulkEditTargets;
}

// The flat line table is owned by the board (not a child) so it stays in sync
// with the toolbar's warehouse/hub focus filters, and its column headers remain
// click-sortable.
function useAssignmentTable({
  filteredLines,
  lineTypes = ALL_LINE_TYPES,
  view,
  selectedIds,
  onSetApproval,
  cartsByShipTo,
  owner,
  ownerName,
  shipToWarehouseId,
  selectedLineId,
  editTargets,
}: TableInput) {
  const gt = useGT();
  const { handleSetStatus, handleSetStatuses, pendingStatusIds } =
    useTrashLine();
  const supplierId = owner.kind === 'po' ? owner.supplierId : undefined;
  const vendorName = owner.kind === 'po' ? ownerName : undefined;
  const sourceWarehouseId =
    owner.kind === 'to' ? owner.sourceWarehouseId : undefined;
  const ownerId = cartOwnerId(owner);
  const tableMeta: CartLineTableMeta = useMemo(
    () => ({
      onSetApproval,
      selectedIds,
      onSetStatus: handleSetStatus,
      onSetStatuses: handleSetStatuses,
      pendingStatusIds,
      cartsByShipTo,
      supplierId,
      vendorName,
      sourceWarehouseId,
      shipToWarehouseId,
      highlightCartKey: `${ownerId}:${shipToWarehouseId ?? ''}`,
      editTargets,
      gt,
    }),
    [
      onSetApproval,
      selectedIds,
      handleSetStatus,
      handleSetStatuses,
      pendingStatusIds,
      cartsByShipTo,
      supplierId,
      vendorName,
      sourceWarehouseId,
      ownerId,
      shipToWarehouseId,
      editTargets,
      gt,
    ],
  );

  // Surface the live approvals to the badge cell via context so it
  // re-renders on every toggle (see CartInclusionContext).
  const cartInclusion = useMemo(
    () => ({ selectedIds, onSetApproval }),
    [selectedIds, onSetApproval],
  );

  const savedColumns = view.config.columns;
  const typedLines = groupLines(filteredLines, lineTypes).lines;
  const columns = useLineTableColumns({
    columns: savedColumns,
    lines: typedLines,
    shipToWarehouseId,
    visibilityKey: columnVisibilityKey(
      lineColumnVisibility(savedColumns, view.columns.columnVisibility),
    ),
  });
  const controlled = useBuyerBoardTableState(
    view.config,
    columns.flatMap((column) => column.id ?? []),
  );
  // The Item column holds the need rows' expand control: with it hidden (or a
  // sort on), the SO lines show as flat rows.
  const itemHidden =
    lineColumnVisibility(savedColumns, view.columns.columnVisibility)
      .product === false;
  const { grouped, rows, needRows, groupProps } = useLineGroupTable(
    filteredLines,
    lineTypes,
    {
      sorted: (controlled.sorting?.length ?? 0) > 0 || itemHidden,
      selectedLineId,
    },
  );
  const table = useDataTable({
    columns,
    data: rows,
    options: {
      initialState: {
        // Effectively disable pagination — the table has no pager UI, and a
        // page-size cap would silently hide rows past the cap.
        pagination: { pageSize: 10000, pageIndex: 0 },
      },
      meta: tableMeta,
      enableMultiSort: true,
      enableColumnResizing: true,
      columnResizeMode: 'onChange',
    },
    controlled,
  });
  return {
    table,
    grouped,
    needRows,
    groupProps,
    cartInclusion,
    editTargets,
  };
}

type TableState = ReturnType<typeof useAssignmentTable>;

function AssignmentLineTable({
  table,
  grouped,
  needRows,
  groupProps,
  cartInclusion,
  editTargets,
  isBulkHighlighted,
  selectedIds,
  selectedLineId,
  onRowClick,
  totals,
}: TableState & {
  isBulkHighlighted: (suggestionId: string) => boolean;
  selectedIds: Set<string>;
  selectedLineId: string | undefined;
  onRowClick: (
    line: AssignmentLine,
    event: ReactMouseEvent<HTMLTableRowElement>,
  ) => void;
  /** Footer totals; null when the open order's header carries them. */
  totals: LineTotals | null;
}) {
  if (grouped.lines.length === 0) {
    return (
      <div className="border-t px-4 py-6 text-center text-sm text-muted-foreground">
        <T context="assignment board no matches for filters">
          No lines match the current filters.
        </T>
      </div>
    );
  }
  // A need row reads as approved once all its open SO lines are.
  const isIncluded = (line: AssignmentLine): boolean => {
    const need = isNeedId(line.suggestionId)
      ? needRows?.needs.get(line.suggestionId)
      : undefined;
    if (!need) return selectedIds.has(line.suggestionId);
    return (
      need.open.length > 0 &&
      need.open.every((l) => selectedIds.has(l.suggestionId))
    );
  };
  return (
    <LineBulkEditProvider value={editTargets}>
      <CartInclusionContext.Provider value={cartInclusion}>
        <NeedRowsContext.Provider value={needRows}>
          <QuantityDraftProvider>
            <DataTable
              table={table}
              density="compact"
              // The detail section is the frame: one rule above the table, none
              // around it. The detail pane scrolls the rows, so the wrapper must
              // not be a scroll box itself, or the sticky head pins to it and
              // scrolls away with the rows. The head is lifted over the rows'
              // positioned selects and badges.
              className="rounded-none border-x-0 border-b-0 overflow-visible [&_thead]:z-10"
              // A cart can hold thousands of lines; draw only those in view.
              virtualize={LINE_ROW_VIRTUALIZATION}
              // Columns keep their content width instead of stretching across a
              // wide screen, like the Gains grid; the spare width stays empty
              // at the right.
              tableProps={{ className: 'w-auto' }}
              {...groupProps(table)}
              onRowClick={onRowClick}
              onRowMouseDown={(_line, event) =>
                preventAssignmentLineModifierSelect(event)
              }
              getRowClassName={(row) =>
                getRowAccentClass(
                  row.original,
                  isIncluded(row.original),
                  row.original.suggestionId === selectedLineId,
                  isBulkHighlighted(row.original.suggestionId),
                )
              }
              getRowAriaSelected={(row) =>
                row.original.suggestionId === selectedLineId
              }
              getRowDomId={(row) => lineRowDomId(row.original.suggestionId)}
            />
            {totals && <LineTotalsRow totals={totals} />}
          </QuantityDraftProvider>
        </NeedRowsContext.Provider>
      </CartInclusionContext.Provider>
    </LineBulkEditProvider>
  );
}

interface DetailHeaderProps {
  cart: CartAssignment;
  stat: CartStat | undefined;
  plan: CartCreatePlan;
  forwardBuy: CartForwardBuySummary | null;
  creating: boolean;
  onClose: () => void;
  onCreateCart: AssignmentBoardProps['onCreateCart'];
}

function AssignmentDetailHeader({
  query,
  owner,
  ownerName,
  ...props
}: DetailHeaderProps & {
  /** The board's line query, which the cart's Orders-table row shares. */
  query: CartLinesQuery;
  owner: CartOwner;
  ownerName: string;
}) {
  return owner.kind === 'po' ? (
    <PurchaseDetailHeader
      {...props}
      query={{ ...query, supplierId: owner.supplierId }}
      title={ownerName}
    />
  ) : (
    <TransferDetailHeader {...props} title={ownerName} />
  );
}

function ReleaseAction({
  cart,
  plan,
  creating,
  onCreateCart,
}: Pick<DetailHeaderProps, 'cart' | 'plan' | 'creating' | 'onCreateCart'>) {
  return (
    <ReleaseCartButton
      creating={creating}
      releaseCount={plan.releaseSuggestionIds.length}
      onCreate={() =>
        onCreateCart(
          cart.shipToWarehouseId,
          plan.summary,
          plan.releaseSuggestionIds,
        )
      }
    />
  );
}

function PurchaseDetailHeader({
  query,
  title,
  cart,
  stat,
  plan,
  forwardBuy,
  creating,
  onClose,
  onCreateCart,
}: DetailHeaderProps & { query: SupplierAssignmentsQuery; title: string }) {
  const slot = useDetailHeaderSlot();
  const cartStat = stat ?? EMPTY_CART_STAT;
  const readiness = useCartOrderReadiness(query, cart.shipToWarehouseId);
  const header = (
    <CartDetailHeader
      embedded={slot !== null}
      title={title}
      shipToName={cart.shipToWarehouseName}
      kind="po"
      lineCount={cartStat.total}
      readiness={readiness}
      approved={plan.approved}
      onClose={onClose}
      forwardBuy={<ForwardBuyCartNote forwardBuy={forwardBuy} />}
      openOrders={
        <OpenDraftPurchaseOrders
          supplierId={query.supplierId}
          shipToWarehouseId={cart.shipToWarehouseId}
        />
      }
      actions={
        <ReleaseAction
          cart={cart}
          plan={plan}
          creating={creating}
          onCreateCart={onCreateCart}
        />
      }
    />
  );
  return slot === null ? header : createPortal(header, slot);
}

/** A transfer order's header: no price, minimum or readiness to show. */
function TransferDetailHeader({
  title,
  cart,
  stat,
  plan,
  creating,
  onClose,
  onCreateCart,
}: DetailHeaderProps & { title: string }) {
  const slot = useDetailHeaderSlot();
  const cartStat = stat ?? EMPTY_CART_STAT;
  const header = (
    <CartDetailHeader
      embedded={slot !== null}
      title={title}
      shipToName={cart.shipToWarehouseName}
      kind="to"
      lineCount={cartStat.total}
      approved={plan.approved}
      onClose={onClose}
      actions={
        <ReleaseAction
          cart={cart}
          plan={plan}
          creating={creating}
          onCreateCart={onCreateCart}
        />
      }
    />
  );
  return slot === null ? header : createPortal(header, slot);
}
